import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog } from '../Dialog'
import { Field } from '../Field'
import { useEntitySave, useEntityDelete, makeEntity, now } from '../../api/hooks/useEntity'
import { ENTITY, type Lesson, type Subject } from '../../types/entities'
import { SCHEDULE_DAY_KEYS } from './ScheduleTable'

/** Диалог создания/редактирования одного урока в расписании. */
export function LessonFormDialog({
  classId,
  day,
  slot,
  lesson,
  subjects,
  onClose,
}: {
  classId: string
  day: number
  slot: number
  lesson?: Lesson
  subjects: Subject[]
  onClose: () => void
}) {
  const { t } = useTranslation()
  const save = useEntitySave<Lesson>(ENTITY.lesson)
  const del = useEntityDelete<Lesson>(ENTITY.lesson)
  const [subjectId, setSubjectId] = useState(lesson?.subjectId ?? subjects[0]?.id ?? '')
  const [startTime, setStartTime] = useState(lesson?.startTime ?? '')
  const [endTime, setEndTime] = useState(lesson?.endTime ?? '')
  const [cabinet, setCabinet] = useState(lesson?.cabinet ?? '')
  const [topic, setTopic] = useState(lesson?.topic ?? '')

  const handleSave = async () => {
    if (!subjectId) return
    await save.mutateAsync(
      makeEntity<Lesson>({
        ...(lesson ?? {}),
        classId,
        subjectId,
        dayOfWeek: day as 1 | 2 | 3 | 4 | 5 | 6 | 7,
        slot,
        startTime: startTime || undefined,
        endTime: endTime || undefined,
        cabinet: cabinet || undefined,
        topic: topic || undefined,
        updatedAt: now(),
      } as Lesson),
    )
    onClose()
  }

  const handleDelete = async () => {
    if (!lesson) return
    await del.mutateAsync(lesson)
    onClose()
  }

  return (
    <Dialog title={lesson ? t('lessonFormDialog.editTitle') : t('lessonFormDialog.newTitle')} onClose={onClose}>
      <div className="text-sm text-gray-500 mb-3">
        {t(`scheduleTable.${SCHEDULE_DAY_KEYS[day - 1]}`)} · {t('lessonFormDialog.slotLabel', { slot })}
      </div>
      <Field label={t('lessonFormDialog.subject')}>
        <select className="input-field" value={subjectId} onChange={(e) => setSubjectId(e.target.value)}>
          <option value="">{t('lessonFormDialog.selectPlaceholder')}</option>
          {subjects.map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
      </Field>
      <div className="grid grid-cols-3 gap-3">
        <Field label={t('lessonFormDialog.start')}>
          <input type="time" className="input-field" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
        </Field>
        <Field label={t('lessonFormDialog.end')}>
          <input type="time" className="input-field" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
        </Field>
        <Field label={t('lessonFormDialog.cabinet')}>
          <input className="input-field" placeholder="212" value={cabinet} onChange={(e) => setCabinet(e.target.value)} />
        </Field>
      </div>
      <Field label={t('lessonFormDialog.topic')}>
        <input className="input-field" value={topic} onChange={(e) => setTopic(e.target.value)} />
      </Field>
      <div className="flex gap-2 justify-between flex-wrap mt-2">
        <div>
          {lesson && (
            <button
              onClick={handleDelete}
              disabled={del.isPending}
              className="text-sm text-red-600 hover:text-red-700 px-3 py-2"
            >
              {t('common.delete')}
            </button>
          )}
        </div>
        <div className="flex gap-2">
          <button onClick={onClose} className="btn-outline text-sm">{t('common.cancel')}</button>
          <button onClick={handleSave} disabled={save.isPending || !subjectId} className="btn-primary text-sm">
            {save.isPending ? '…' : t('common.save')}
          </button>
        </div>
      </div>
    </Dialog>
  )
}
